import type { HotStackUiPhase } from '@frontend/types/hotloading';
import { stackClickTargetsRefresh } from '@frontend/features/hotloading/runtimeHotloadingState';

interface HotloadingControlGroupProps {
  stackPhase: HotStackUiPhase;
  routeMode: 'hot' | 'parity';
  routeToggleEnabled: boolean;
  routeToHotAllowed: boolean;
  routeBusy: boolean;
  stackBusy: boolean;
  error?: string;
  onStackToggle: () => void;
  onRouteToggle: () => void;
}

function stackLabel(phase: HotStackUiPhase, needsRefresh: boolean): string {
  if (needsRefresh && phase === 'up') {
    return 'Stack unhealthy';
  }
  switch (phase) {
    case 'loading':
      return 'Stack loading';
    case 'up':
      return 'Stack up';
    case 'unloading':
      return 'Stack unloading';
    default:
      return 'Stack down';
  }
}

function stackTitle(phase: HotStackUiPhase, routeMode: 'hot' | 'parity', needsRefresh: boolean): string {
  if (needsRefresh && phase === 'up') {
    return 'Stack unhealthy — click to refresh (compose down + force-recreate)';
  }
  if (phase === 'up' && routeMode === 'hot') {
    return 'Switch route to parity before stopping the hot stack';
  }
  switch (phase) {
    case 'loading':
      return 'Starting hot stack (docker compose up)';
    case 'up':
      return 'Stop hot stack (compose down; route must be parity first)';
    case 'unloading':
      return 'Stopping hot stack';
    default:
      return 'Start hot stack (docker compose up; does not change Caddy route)';
  }
}

function routeLabel(routeMode: 'hot' | 'parity', routeBusy: boolean): string {
  if (routeBusy) {
    return 'Route…';
  }
  return routeMode === 'hot' ? 'Route hot' : 'Route parity';
}

export default function HotloadingControlGroup({
  stackPhase,
  routeMode,
  routeToggleEnabled,
  routeToHotAllowed,
  routeBusy,
  stackBusy,
  error,
  onStackToggle,
  onRouteToggle
}: HotloadingControlGroupProps) {
  const needsRefresh = stackClickTargetsRefresh({
    stackUiPhase: stackPhase,
    routeToHotAllowed
  });
  const stackIsDown = stackPhase === 'down';
  const stackIsUp = stackPhase === 'up' || stackPhase === 'loading' || stackPhase === 'unloading';
  const stackTransition = stackPhase === 'loading' || stackPhase === 'unloading';
  const routeActive = routeMode === 'hot';
  // Enabled whenever stack is up; switching *to* hot is validated in the click handler (routeToHotAllowed).
  const routeDisabled = !routeToggleEnabled || routeBusy || stackTransition;
  const routeTitle =
    routeMode === 'hot'
      ? 'Send site traffic to parity (…0000); hot containers keep running'
      : routeToHotAllowed
        ? 'Send site traffic to hot stack (…0001)'
        : 'Hot stack is starting or not healthy yet — wait, then try again';
  const stackDisabled =
    stackBusy || routeBusy || stackTransition || (routeMode === 'hot' && stackPhase === 'up' && !needsRefresh);

  return (
    <div className="hotloading-control-group">
      <div className="hotloading-control-group-label">Testing runtime</div>
      {error ? <div className="hotloading-control-group-error settings-error">{error}</div> : null}
      <div className="hotloading-control-group-row">
        <button
          type="button"
          className={`hotloading-badge hotloading-badge-stack ${stackIsDown ? 'is-down' : ''} ${stackIsUp ? 'is-up' : ''} ${stackTransition ? 'is-busy' : ''} ${needsRefresh && stackPhase === 'up' ? 'is-unhealthy' : ''}`}
          title={stackTitle(stackPhase, routeMode, needsRefresh)}
          disabled={stackDisabled}
          onClick={onStackToggle}
        >
          {stackLabel(stackPhase, needsRefresh)}
        </button>
        <button
          type="button"
          className={`hotloading-badge hotloading-badge-route ${routeActive ? 'is-active' : ''} ${routeBusy ? 'is-busy' : ''}`}
          title={routeTitle}
          disabled={routeDisabled}
          onClick={onRouteToggle}
        >
          {routeLabel(routeMode, routeBusy)}
        </button>
      </div>
    </div>
  );
}
